import React, { useState } from "react";
import PageBanner from "../components/PageBanner";
import useReveal from "../hooks/useReveal";
import { COMPANY, IMAGES } from "../mock";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Honeypot } from "../components/Honeypot";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";

export default function Contact() {
  useReveal();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [website, setWebsite] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, source: "contact-form", website });
      setSubmitted(true);
      toast({ title: "Message sent", description: "We'll get back to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
  };

  const info = [
    { icon: Phone, label: "Phone — Slava", value: COMPANY.contacts[0].phone, href: COMPANY.contacts[0].href },
    { icon: Phone, label: "Phone — Andrew", value: COMPANY.contacts[1].phone, href: COMPANY.contacts[1].href },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Location", value: COMPANY.city },
    { icon: Clock, label: "Hours", value: COMPANY.hours },
  ];

  return (
    <>
      <Seo title="Contact" path="/contact" description="Contact Classic Interior Renovations in Edmonton for a free estimate on your kitchen, bathroom, basement or full-home renovation project." />
      <PageBanner
        crumb="Contact"
        title="Let's build something together"
        subtitle="Fill out the form below and we'll reach out to learn more about your project."
        image={IMAGES.kitchen4}
      />

      <section className="py-24 bg-white overflow-hidden">
        <div className="container-x grid lg:grid-cols-2 gap-16">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Get in touch</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight mb-6">
              We'd love to hear about your project
            </h2>
            <p className="text-[#6b7683] text-lg leading-relaxed mb-10">
              Whether it's a kitchen refresh, a full home remodel or a commercial fit-out, our team is here to help you plan every detail.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {info.map((it) => (
                <div key={it.label} className="flex gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#eaf5fd] flex items-center justify-center">
                    <it.icon className="w-6 h-6 text-[#2f9fe6]" />
                  </div>
                  <div>
                    <div className="text-sm text-[#9aa4af] mb-1">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} className="font-medium text-[#0f2740] hover:text-[#2f9fe6] transition-colors break-all">{it.value}</a>
                    ) : (
                      <div className="font-medium text-[#0f2740]">{it.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal-right">
            <div className="bg-[#f7f9fb] rounded-3xl p-8 sm:p-10 border border-[#eef1f4]">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-[#2f9fe6] mx-auto mb-4" />
                  <h3 className="font-display text-2xl font-semibold text-[#0f2740] mb-2">Thank you!</h3>
                  <p className="text-[#6b7683]">Your message has been received. We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <Honeypot value={website} onChange={setWebsite} />
                  <div className="space-y-1.5">
                    <Label htmlFor="cname">Full name</Label>
                    <Input id="cname" required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" className="bg-white" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="cemail">Email</Label>
                      <Input id="cemail" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="bg-white" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cphone">Phone</Label>
                      <Input id="cphone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(780) 000-0000" className="bg-white" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cmsg">Project details</Label>
                    <Textarea id="cmsg" rows={5} required value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your space and goals..." className="bg-white" />
                  </div>
                  <button type="submit" className="w-full py-4 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors">
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
